import {
  ApplicationIcon,
  ButtonIcon,
  Chip,
  Icon,
  ListRow,
  ListRowGroup,
  Loader,
  Menu,
  Tooltip,
  Typography,
} from "@saviynt/design-system";
import { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useRouting } from "../contexts/RoutingContext";
import {
  getApplicationDisplayName,
  getApplicationLogo,
} from "../utils/integrationIcons";
import { DeleteIntegrationModal } from "./DeleteIntegrationModal";

interface Integration {
  id: string;
  name: string;
  application: string;
  status: "Active" | "Failed" | "Pending";
  isEnabled?: boolean;
}

interface IntegrationsListProps {
  integrations: Integration[];
  selectedIntegrationId: string | null;
  onRowClick: (integrationId: string) => void;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
}

export function IntegrationsList({
  integrations,
  selectedIntegrationId,
  onRowClick,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}: IntegrationsListProps) {
  const intl = useIntl();
  const { handleEditIntegration } = useRouting();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [integrationToDelete, setIntegrationToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const handleEditClick = (
    integrationId: string,
    application: string,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    handleEditIntegration(integrationId, application);
  };

  const handleDeleteClick = (
    integrationId: string,
    integrationName: string,
  ) => {
    setIntegrationToDelete({ id: integrationId, name: integrationName });
    setIsDeleteModalOpen(true);
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
    setIntegrationToDelete(null);
  };

  const handleEndReached = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage && fetchNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const observerTarget = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasNextPage || !observerTarget.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetchingNextPage) {
          handleEndReached();
        }
      },
      {
        threshold: 0.1,
        rootMargin: "0px",
      },
    );

    const currentTarget = observerTarget.current;
    observer.observe(currentTarget);

    return () => {
      observer.unobserve(currentTarget);
    };
  }, [handleEndReached, hasNextPage, isFetchingNextPage]);

  return (
    <div
      className="flex flex-col"
      style={{ height: "min(72vh, calc(100dvh - 14rem))" }}
    >
      <ListRowGroup increaseViewportBy={320} className="pl-12">
        {integrations.map((integration) => (
          <ListRow
            key={integration.id}
            centerContent={
              <div className="flex-1 flex flex-col">
                <div className="flex items-center gap-2">
                  <Typography
                    kind="h3"
                    lineLimit={1}
                    tooltipConfigs={{ isPortalEnabled: true }}
                  >
                    {integration.name}
                  </Typography>
                  {integration.isEnabled === false && (
                    <Chip
                      label={intl.formatMessage({
                        id: "Tm6rqe",
                        defaultMessage: "Draft",
                        description: "Draft status label",
                      })}
                      kind="outlined"
                      color="warning"
                      size="xSmall"
                    />
                  )}
                </div>
                <Typography
                  kind="body2"
                  lineLimit={1}
                  tooltipConfigs={{ isPortalEnabled: true }}
                >
                  <div className="flex items-center gap-2">
                    <ApplicationIcon
                      logo={getApplicationLogo(integration.application)}
                      size="xSmall"
                    />
                    {getApplicationDisplayName(integration.application)}
                  </div>
                </Typography>
              </div>
            }
            trailingContent={
              selectedIntegrationId === integration.id ? null : (
                <div className="flex items-center gap-4 px-4">
                  <Chip
                    label={integration.status}
                    color={
                      integration.status === "Active"
                        ? "success"
                        : integration.status === "Pending"
                          ? "warning"
                          : "critical"
                    }
                    kind="subtleFilled"
                    size="small"
                  />
                  <Tooltip
                    text={intl.formatMessage({
                      id: "3NqNiF",
                      defaultMessage: "Edit Integration",
                      description: "Edit integration button label",
                    })}
                    isPortalEnabled
                  >
                    <ButtonIcon
                      kind="outlinedNeutral"
                      size="medium"
                      icon={<Icon kind="Edit" size="small" />}
                      ariaLabel={intl.formatMessage({
                        id: "3NqNiF",
                        defaultMessage: "Edit Integration",
                        description: "Edit integration button label",
                      })}
                      onClick={(e) =>
                        handleEditClick(
                          integration.id,
                          integration.application,
                          e,
                        )
                      }
                    />
                  </Tooltip>
                  <Menu
                    isPortalEnabled
                    options={[
                      {
                        value: "delete",
                        label: intl.formatMessage({
                          id: "PSTccr",
                          defaultMessage: "Delete Integration",
                          description: "Delete integration menu item",
                        }),
                      },
                    ]}
                    onChange={(option) => {
                      if (option.value === "delete") {
                        handleDeleteClick(integration.id, integration.name);
                      }
                    }}
                    trigger={
                      <ButtonIcon
                        kind="ghost"
                        size="small"
                        icon={<Icon kind="MoreVertical" size="small" />}
                        ariaLabel={intl.formatMessage({
                          id: "5eGg8J",
                          defaultMessage: "More options",
                          description: "Aria label for kebab menu trigger",
                        })}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      />
                    }
                  />
                </div>
              )
            }
            handleRowClick={() => onRowClick(integration.id)}
            isRowDetailsOpen={selectedIntegrationId === integration.id}
            isCheckboxDisabled
            connectorDistance="3.125rem"
            isRowConnectTransitionEnabled
          />
        ))}
      </ListRowGroup>
      {hasNextPage && (
        <div ref={observerTarget} className="h-20 relative">
          {isFetchingNextPage && (
            <div className="flex items-center justify-center py-4">
              <Loader kind="loop" format="inline" color="brand" />
            </div>
          )}
        </div>
      )}
      <DeleteIntegrationModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteModalClose}
        integrationId={integrationToDelete?.id || ""}
        integrationName={integrationToDelete?.name}
      />
    </div>
  );
}
